import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Breadcrumb from '../components/Breadcrumb';
import Badge from '../components/Badge';
import SEO from '../components/SEO';
import { getJobById } from '../data/jobs';
import './JobDetailPage.css';

const MONTHS = {
  Jan: '01',
  Feb: '02',
  Mar: '03',
  Apr: '04',
  May: '05',
  Jun: '06',
  Jul: '07',
  Aug: '08',
  Sep: '09',
  Oct: '10',
  Nov: '11',
  Dec: '12',
};

const InfoRow = ({ label, value }) => (
  <tr className="info-row">
    <td className="info-label">{label}</td>
    <td className="info-value">{value}</td>
  </tr>
);

const DateRow = ({ label, value, highlight }) => (
  <div className="date-row">
    <span className="date-label">{label}</span>
    <span className={`date-value ${highlight ? 'date-highlight' : ''}`}>{value}</span>
  </div>
);

const toIsoDate = (value) => {
  if (!value || typeof value !== 'string') return null;

  const trimmed = value.trim();
  const match = /^(\d{2})-([A-Za-z]{3})-(\d{4})$/.exec(trimmed);
  if (!match) return null;

  const [, day, month, year] = match;
  const numericMonth = MONTHS[month];
  if (!numericMonth) return null;

  return `${year}-${numericMonth}-${day}`;
};

const getOrganizationName = (job) => {
  if (job.organization && job.organization.trim()) return job.organization.trim();

  const titlePrefix = job.title?.split(' Recruitment')[0]?.trim();
  return titlePrefix || 'Recruiting Organization';
};

const getStructuredSalary = (salary) => {
  if (!salary || typeof salary !== 'string') return null;

  const numericValues = salary.match(/[\d,]+/g);
  if (!numericValues || numericValues.length === 0) return null;

  const cleanedValues = numericValues
    .map((value) => Number(value.replace(/,/g, '')))
    .filter((value) => Number.isFinite(value) && value > 0);

  if (cleanedValues.length === 0) return null;

  const salaryValue = {
    '@type': 'QuantitativeValue',
    unitText: 'MONTH',
  };

  if (cleanedValues.length === 1) {
    salaryValue.value = cleanedValues[0];
  } else {
    salaryValue.minValue = Math.min(...cleanedValues);
    salaryValue.maxValue = Math.max(...cleanedValues);
  }

  return {
    '@type': 'MonetaryAmount',
    currency: 'INR',
    value: salaryValue,
  };
};

const getDescriptionParagraphs = (value) =>
  (value || '')
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

const JobDetailPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const job = getJobById(jobId);

  if (!job) {
    return (
      <div className="job-detail-not-found">
        <SEO title="Job Not Found" description="The requested job could not be found." noIndex />
        <h2>Job not found</h2>
        <button onClick={() => navigate('/')}>Back to Home</button>
      </div>
    );
  }

  const organizationName = getOrganizationName(job);
  const postingDate =
    toIsoDate(job.importantDates?.find((item) => item.label.toLowerCase().includes('notification'))?.value);
  const validThrough =
    toIsoDate(job.importantDates?.find((item) => item.label.toLowerCase().includes('last'))?.value) ||
    toIsoDate(job.lastDate);
  const structuredSalary = getStructuredSalary(job.salary);
  const descriptionParagraphs = getDescriptionParagraphs(job.description);
  const structuredDescription = descriptionParagraphs.join(' ');

  const breadcrumbs = [
    { label: 'Home', to: '/' },
    { label: job.title },
  ];

  const jobPostingSchema = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: job.title,
    description: structuredDescription,
    identifier: {
      '@type': 'PropertyValue',
      name: organizationName,
      value: job.id,
    },
    hiringOrganization: {
      '@type': 'Organization',
      name: organizationName,
      sameAs: 'https://sarkarinaukrirajasthan.com/',
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressRegion: job.location || 'India',
        addressCountry: 'IN',
      },
    },
    employmentType: 'FULL_TIME',
    ...(postingDate ? { datePosted: postingDate } : {}),
    ...(validThrough ? { validThrough } : {}),
    ...(structuredSalary ? { baseSalary: structuredSalary } : {}),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://sarkarinaukrirajasthan.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: job.title,
        item: `https://sarkarinaukrirajasthan.com/job/${jobId}`,
      },
    ],
  };

  const structuredData = [jobPostingSchema, breadcrumbSchema];
  const seoDescription = [
    `${job.title} at ${organizationName}.`,
    job.posts ? `${job.posts} vacancies.` : null,
    job.location ? `Location: ${job.location}.` : null,
    job.lastDate ? `Apply before ${job.lastDate}.` : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <article className="job-detail-page" itemScope itemType="https://schema.org/JobPosting">
      <SEO
        title={job.title}
        description={seoDescription}
        path={`/job/${jobId}`}
        structuredData={structuredData}
      />
      <Breadcrumb items={breadcrumbs} />

      <div className="job-detail-hero">
        <div className="job-hero-bg-pattern" />
        <div className="job-hero-content">
          <div className="job-hero-tag">{organizationName}</div>
          <h1 className="job-hero-title">{job.title}</h1>
        </div>
      </div>

      <div className="job-quick-info">
        <div className="quick-info-badges">
          <Badge variant="location" icon="Loc">{job.location}</Badge>
          <Badge variant="salary" icon="Rs">{job.salary}</Badge>
          <Badge variant="count" icon="Posts">
            {job.posts ? job.posts.toLocaleString() : 'Various'} Posts
          </Badge>
        </div>
        {job.links && job.links.length > 0 && (
          <a
            href={job.links.find((link) => link.label.toLowerCase().includes('apply'))?.url || job.links[0].url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-apply"
          >
            Apply Online Now
          </a>
        )}
      </div>

      <div className="job-detail-grid">
        <div className="job-detail-left">
          <section className="detail-section">
            <div className="section-header">
              <span className="section-icon">Info</span>
              <h2 className="section-title">Job Description</h2>
            </div>
            {descriptionParagraphs.map((paragraph, index) => (
              <p key={index} className="section-text">
                {paragraph}
              </p>
            ))}

            {job.eligibility && (
              <>
                <h3 className="sub-section-title">Eligibility Criteria</h3>
                <ul className="eligibility-list">
                  {job.eligibility.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </>
            )}
          </section>

          {job.details && job.details.length > 0 && (
            <section className="detail-section">
              <div className="section-header">
                <span className="section-icon">Details</span>
                <h2 className="section-title">Important Details (At a Glance)</h2>
              </div>
              <table className="details-table">
                <tbody>
                  {job.details.map((row, index) => (
                    <InfoRow key={index} label={row.label} value={row.value} />
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </div>

        <div className="job-detail-right">
          {job.importantDates && job.importantDates.length > 0 && (
            <div className="dates-card">
              <div className="dates-card-header">
                <span>Date</span>
                <h3 className="dates-title">Important Dates</h3>
              </div>
              <div className="dates-list">
                {job.importantDates.map((item, index) => (
                  <DateRow
                    key={index}
                    label={item.label}
                    value={item.value}
                    highlight={
                      item.label.toLowerCase().includes('last') ||
                      item.label.toLowerCase().includes('start')
                    }
                  />
                ))}
              </div>
            </div>
          )}

          {job.links && job.links.filter((link) => !link.label.toLowerCase().includes('source article')).length > 0 && (
            <div className="useful-links-card">
              <h3 className="useful-links-title">Useful Links</h3>
              <div className="useful-links-list">
                {job.links
                  .filter((link) => !link.label.toLowerCase().includes('source article'))
                  .map((link, index) => (
                    <a
                      key={index}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="useful-link-btn"
                    >
                      Link {index + 1}: {link.label}
                    </a>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default JobDetailPage;
